import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  inject,
  Renderer2,
  signal,
} from '@angular/core';
import { RouterLink, RouterLinkActive } from '@angular/router';
import { GrooveSurfaceDirective } from '@groove/angular';
import { NAV_GROUPS, type NavGroup } from '../nav-data';

// Disclosure semantics, not a menu: the entries are navigation LINKS, so the
// WAI-ARIA disclosure pattern (button aria-expanded/aria-controls revealing a
// region of Tab-reachable <a>s) is correct. role=menu/menuitem (what
// @angular/aria's ngMenu provides) implies an application command menu with
// roving arrow-key focus — wrong semantic for a site nav of links. Hand-rolled.
@Component({
  selector: 'app-top-nav',
  imports: [RouterLink, RouterLinkActive, GrooveSurfaceDirective],
  templateUrl: './top-nav.component.html',
  styleUrl: './top-nav.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '(keydown.escape)': 'onEscape()',
    '(focusout)': 'onFocusOut($event)',
  },
})
export class TopNavComponent {
  protected readonly navGroups = NAV_GROUPS;

  /** Label of the currently open group, or null when every panel is closed. */
  protected readonly openGroup = signal<string | null>(null);

  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly renderer = inject(Renderer2);
  /** Teardown for the document click listener; null while no panel is open. */
  private outsideClickTeardown: (() => void) | null = null;

  constructor() {
    inject(DestroyRef).onDestroy(() => {
      this.detachOutsideClick();
    });
  }

  protected panelId(g: NavGroup): string {
    return this.panelDomId(g.label);
  }

  private panelDomId(label: string): string {
    return `tn-panel-${label.toLowerCase().replace(/\s+/g, '-')}`;
  }

  /**
   * Escape closes the open panel AND returns focus to its trigger. Without the
   * explicit refocus, focus would be on a link inside the now-hidden panel and
   * the browser drops it to <body> (lost focus — a WCAG 2.4.3 failure).
   */
  protected onEscape(): void {
    const open = this.openGroup();
    if (!open) {
      return;
    }
    const trigger = this.host.nativeElement.querySelector<HTMLElement>(
      `[aria-controls="${this.panelDomId(open)}"]`,
    );
    this.close();
    trigger?.focus();
  }

  /**
   * Close the open panel when focus leaves its group (APG disclosure-nav: Tabbing
   * out of the dropdown dismisses it). Focus moving between the trigger and its
   * own panel keeps it open; focus to another trigger, a sibling link, or out of
   * the nav closes it.
   *
   * A null `relatedTarget` is NOT proof that focus left (#82): the spec only
   * guarantees it names the destination when there is a focusable one. Rather
   * than encode per-engine null semantics, on null we defer one frame so the
   * browser finishes moving focus, then judge by where it actually landed —
   * open stays open only if focus is really inside the group. Live WebKit
   * probes (CI node, 2026-08-17): macOS WebKit skips links on plain Tab
   * entirely (Safari convention — focus goes to <body> with a null
   * relatedTarget, and the deferred check correctly closes), while Safari's
   * link-navigation gesture Option+Tab delivers the destination link
   * synchronously like Chromium/Firefox do. The unit suite pins the deferral
   * contract with a simulated null-then-lands-inside sequence.
   */
  protected onFocusOut(event: FocusEvent): void {
    const open = this.openGroup();
    if (!open) {
      return;
    }
    const panel = this.host.nativeElement.querySelector(
      `[id="${this.panelDomId(open)}"]`,
    );
    const group = panel?.closest('.top-nav-group');
    const next = event.relatedTarget;
    if (next instanceof Node) {
      if (!group?.contains(next)) {
        this.close();
      }
      return;
    }
    requestAnimationFrame(() => {
      if (!this.openGroup()) {
        return;
      }
      const active = document.activeElement;
      if (!(active instanceof Node) || !group?.contains(active)) {
        this.close();
      }
    });
  }

  /** Last group right-aligns its panel so it never overflows the viewport. */
  protected isLast(g: NavGroup): boolean {
    return this.navGroups[this.navGroups.length - 1]?.label === g.label;
  }

  protected toggle(label: string): void {
    const next = this.openGroup() === label ? null : label;
    this.setOpenGroup(next);
  }

  protected close(): void {
    this.setOpenGroup(null);
  }

  /**
   * Single choke point for opening/closing a panel. The `hidden` attribute on
   * every panel is applied here IMPERATIVELY and SYNCHRONOUSLY via Renderer2
   * — not through the template's `[hidden]` binding, which only used to
   * update on Angular's own change-detection pass (a signal write scheduling
   * CD is not guaranteed to have flushed to the DOM/render-tree by the time
   * the very next keyboard event is handled).
   *
   * That async gap was a real, WebKit-only a11y bug: WebKit derives tab order
   * from its render tree, and on a slower run a keyboard Tab pressed right
   * after opening a panel could still observe the panel as `hidden`, landing
   * focus past the whole disclosure. `onFocusOut` then saw focus leave the
   * group and closed the very panel Tab was supposed to enter. Writing the
   * attribute here (synchronously, in the same task as the triggering
   * keydown/click) and forcing a reflow removes the gap entirely: by the time
   * this call returns, both the DOM and the render tree already reflect the
   * new open/closed state, so a Tab dispatched immediately after always sees
   * the true, current tab order. The signal (`openGroup`) is kept in sync in
   * the same call for the other template bindings (`aria-expanded`, etc.)
   * that aren't tab-order-critical and don't need this guarantee.
   */
  private setOpenGroup(next: string | null): void {
    this.openGroup.set(next);
    for (const g of this.navGroups) {
      if (g.kind === 'link') {
        continue;
      }
      const panel = this.host.nativeElement.querySelector<HTMLElement>(
        `#${CSS.escape(this.panelDomId(g.label))}`,
      );
      if (!panel) {
        continue;
      }
      if (g.label === next) {
        this.renderer.removeAttribute(panel, 'hidden');
      } else {
        this.renderer.setAttribute(panel, 'hidden', '');
      }
    }
    // Force a synchronous style/layout flush (see doc comment above).
    // eslint-disable-next-line @typescript-eslint/no-meaningless-void-operator -- reading offsetHeight has a real DOM side effect (forces reflow); the value itself is intentionally discarded
    void this.host.nativeElement.offsetHeight;

    if (next === null) {
      this.detachOutsideClick();
    } else {
      this.attachOutsideClick();
    }
  }

  private attachOutsideClick(): void {
    if (this.outsideClickTeardown) {
      return;
    }
    this.outsideClickTeardown = this.renderer.listen(
      'document',
      'click',
      (event: Event) => {
        const target = event.target;
        if (
          target instanceof Node &&
          !this.host.nativeElement.contains(target)
        ) {
          this.close();
        }
      },
    );
  }

  private detachOutsideClick(): void {
    this.outsideClickTeardown?.();
    this.outsideClickTeardown = null;
  }
}
